// src/services/location.js
//
// 🟢 LOCATION MESSAGES (web) — byte-compatible with the phone's sendLocationMessage.
//
// The contract is unusual and easy to get wrong, so it is spelled out here:
//
//   • The Firestore doc carries NO latitude/longitude AT ALL. The coordinates travel
//     ONLY inside `locationPerDeviceKeys` — a per-device nacl.box of the JSON string
//     `{"lat":<number>,"lng":<number>}`. (ChatModal.sendLocationMessage ~22190.)
//   • The keys are `lat` / `lng`, NOT latitude/longitude, and CryptoService.unsealLocation
//     hard-requires `typeof parsed.lat === 'number'` — a string "51.5" is rejected and
//     the recipient renders nothing. Nominatim returns strings, so everything is
//     parseFloat'd before sealing.
//   • Extra keys in that JSON are harmless: the phone reads lat/lng and ignores the
//     rest, so we can carry a human-readable `name` for web-to-web display.
//   • If sealing fails the phone REFUSES to send rather than shipping a location with
//     no location (its "0,0 Gulf of Guinea" bug). We do the same.
//
// Live location is deliberately not implemented here — the web never publishes to
// `liveLocations`, and every doc goes out with live:false.

import {
  collection, doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { sealForUsers } from '../crypto/e2ee';
import { relayNewMessage } from './socket';
import { writeChatDoc, newTempId } from './mediaUpload';
import { sealMembers } from './groupMembers';
import { setMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy

/** iOS/macOS get Apple Maps, everyone else Google — the same split the app makes
 *  (MessageBubble ~362 uses maps.apple.com on iOS). */
export function mapsUrlFor(lat, lng, label) {
  const ua = navigator.userAgent || '';
  const isApple = /iPhone|iPad|iPod|Macintosh/.test(ua);
  if (isApple) {
    const q = label ? `&q=${encodeURIComponent(label)}` : '';
    return `http://maps.apple.com/?ll=${lat},${lng}${q}`;
  }
  return `https://maps.google.com/?q=${lat},${lng}`;
}

/** Browser geolocation, promisified with a sane timeout. */
export function getCurrentPosition({ timeout = 12000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('This browser has no location support.')); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(new Error(
        err.code === 1 ? 'Location permission was denied. Allow it in the address bar to share your position.'
          : err.code === 3 ? "Couldn't get a fix in time. Try again."
            : 'Location is unavailable right now.'
      )),
      { enableHighAccuracy: true, timeout, maximumAge: 30000 }
    );
  });
}

// ── geocoding ─────────────────────────────────────────────────────────────
// OpenStreetMap's Nominatim: no API key, CORS-enabled, fine for the occasional
// lookup a person makes by hand. (The app uses Google Places with a key from its
// native config; that key isn't available to the browser bundle.)
const NOMINATIM = 'https://nominatim.openstreetmap.org';

export async function searchPlaces(q, { signal } = {}) {
  const term = (q || '').trim();
  if (term.length < 2) return [];
  const url = `${NOMINATIM}/search?format=jsonv2&addressdetails=1&limit=8&q=${encodeURIComponent(term)}`;
  const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Search is unavailable right now.');
  const rows = await res.json();
  return (Array.isArray(rows) ? rows : []).map((r) => {
    const parts = String(r.display_name || '').split(',').map((s) => s.trim());
    return {
      id: `${r.osm_type || 'n'}${r.osm_id || r.place_id}`,
      // 🟢 numbers, not strings — unsealLocation type-checks them on the phone
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
      name: r.name || parts[0] || term,
      address: parts.slice(1).join(', ') || r.display_name || '',
    };
  }).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
}

/** 🟢 "Dearborn, MI" — what the chat list wants, not a full postal address and not raw
 *  coordinates. Cached per rounded coordinate: a chat row re-renders constantly and
 *  Nominatim is a courtesy service, so it must be asked once per place, not per paint. */
const _placeCache = new Map();
export async function shortPlaceLabel(lat, lng) {
  const key = `${Number(lat).toFixed(3)},${Number(lng).toFixed(3)}`;
  if (_placeCache.has(key)) return _placeCache.get(key);
  const pending = (async () => {
    try {
      const url = `${NOMINATIM}/reverse?format=jsonv2&zoom=14&lat=${lat}&lon=${lng}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const r = await res.json();
      const a = r.address || {};
      const town = a.city || a.town || a.village || a.suburb || a.county || a.hamlet;
      // "US-MI" → "MI"; otherwise fall back to the full region name
      const iso = a['ISO3166-2-lvl4'] || a['ISO3166-2-lvl6'] || '';
      const region = (iso.includes('-') ? iso.split('-').pop() : '') || a.state || a.country;
      if (town && region) return `${town}, ${region}`;
      return town || region || a.country || null;
    } catch {
      return null;
    }
  })();
  _placeCache.set(key, pending);
  const out = await pending;
  _placeCache.set(key, out); // replace the promise with the value
  return out;
}

export async function reverseGeocode(lat, lng, { signal } = {}) {
  try {
    const url = `${NOMINATIM}/reverse?format=jsonv2&zoom=18&lat=${lat}&lon=${lng}`;
    const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const r = await res.json();
    const parts = String(r.display_name || '').split(',').map((s) => s.trim());
    return { name: r.name || parts[0] || 'Current location', address: parts.slice(1).join(', ') };
  } catch {
    return null;
  }
}

// ── send ──────────────────────────────────────────────────────────────────
export async function sendLocationMessage({ chat, myUid, myName, lat, lng, name, reply }) {
  if (!chat?.id) throw new Error('no chat');
  const _lat = Number(lat);
  const _lng = Number(lng);
  if (!Number.isFinite(_lat) || !Number.isFinite(_lng)) throw new Error('bad coordinates');

  const participants = sealMembers(chat);   // 🔐 active members only
  const isGroup = !!chat.isGroup;
  const clientTempId = newTempId();

  // seal FIRST — a location whose coordinates couldn't be sealed is not worth sending
  const payload = JSON.stringify({ lat: _lat, lng: _lng, ...(name ? { name } : {}) });
  const sealed = await sealForUsers(payload, participants);
  if (!sealed || !Object.keys(sealed.perDeviceKeys || {}).length) {
    throw new Error("Couldn't secure the coordinates — the location was not sent.");
  }

  const msgRef = doc(collection(db, 'chats', chat.id, 'messages'));
  const messageId = msgRef.id;

  await setMessageDoc(msgRef, {
    type: 'location',
    clientTempId,
    locationPerDeviceKeys: sealed.perDeviceKeys,
    senderDeviceId: sealed.senderDeviceId,
    senderId: myUid,
    timestamp: serverTimestamp(),
    live: false,          // the web never shares live location
    liveUntil: null,
    liveLocationId: null,
    sent: true,
    delivered: false,
    seen: false,
    ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
    // the sealed quote — an object in Firestore, an object on the wire (the server
    // re-stringifies replyTo itself); the two *PerDeviceKeys maps must stay objects
    ...(reply || {}),
  });

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
    cloudText: '📍 Location', clientMsgId: clientTempId,
    extra: {
      type: 'location',
      locationPerDeviceKeys: sealed.perDeviceKeys,
      senderDeviceId: sealed.senderDeviceId,
      live: false,
      ...(isGroup ? { senderName: myName || 'Web' } : {}),
      ...(reply || {}),
    },
  });

  await writeChatDoc({ chat, myUid, myName, messageId, preview: '📍 Location' });
  return messageId;
}
